import {
  applyAnnotationsToSchema,
  generateArraySchema,
  generateColorSchema,
  generateContainerSchema,
  generateRootObjectSchema,
  generateStringSchema,
} from "components/cards/common/schema";
import { BaseAtomSpec, generateAtomContainerSchema } from "components/cards/atoms/BaseAtomSpec";
import {
  ButtonParticleSpec,
  ImageParticleSpec,
  TextParticleSpec,
} from "components/cards/particles/YinzCam";

const ACCENT = 'var(--theme-mode-accent-color)';
const FG = 'var(--theme-mode-foreground-color)';
const BG = 'var(--theme-mode-background-color)';
const HIGHLIGHT = 'var(--theme-mode-highlight-color)';

export const specification = new BaseAtomSpec("Cricket Match Header", generateRootObjectSchema({
  // ── MatchHeaderBase props (inlined from janus4) ──
  container: generateAtomContainerSchema({
    defaultValues: { background: { visible: true }, themeMode: "primary" },
  }),
  matchStatus: generateStringSchema("Match Status", "The current status of the match.", {
    choices: ['pre', 'live', 'post'],
    choiceTitles: ['Pre-Match', 'Live', 'Post-Match'],
  }),

  // Match info elements
  txtCompetitionName: TextParticleSpec.generateSchema(
    "Competition Name",
    "Feed driven. The competition or series name.",
    { defaultValues: { size: '1rem', weight: '600', transform: 'uppercase', color: FG } }
  ),
  txtDateRange: TextParticleSpec.generateSchema(
    "Date Range",
    "Feed driven. The match date/time range.",
    { defaultValues: { size: '0.875rem', weight: '400', color: FG } }
  ),
  txtLocationLabel: TextParticleSpec.generateSchema(
    "Location Label",
    "Feed driven. The venue name.",
    { defaultValues: { size: '1rem', weight: '600', color: FG } }
  ),
  txtMatchStateLabel: TextParticleSpec.generateSchema(
    "Match State Label",
    "Pre / live / post label rendered as a badge.",
    {
      defaultValues: {
        size: '0.7rem',
        weight: '600',
        transform: 'uppercase',
        wrap: 'nowrap',
        // Default the badge text to white; the theme background color is used when the mode defines
        // one, but it has no value in this header's mode, so without the fallback the text isn't white.
        color: 'var(--theme-mode-background-color, #fff)',
        // border is enabled with no line (style: none) purely to round the badge: BaseParticle only
        // applies --particle-container-border-radius under .particle-with-border (border.visible).
        container: {
          padding: '6px 8px',
          // Fallback to the design's navy when the theme mode doesn't define an accent color,
          // otherwise the badge background resolves to nothing and disappears entirely.
          background: { visible: true, color: 'var(--theme-mode-accent-color, #223163)' },
          // radius >= half the badge height makes a pill; border has no line (style: none) and exists
          // only so BaseParticle applies --particle-container-border-radius (.particle-with-border).
          border: { visible: true, style: 'none', width: '0', radius: '999px' },
        },
      },
    }
  ),
  txtMatchStateLabelTablet: TextParticleSpec.generateSchema(
    "Match State Label (Tablet)",
    "Pre / live / post label rendered as a badge. Used on tablet breakpoint (601-1000px).",
    {
      defaultValues: {
        size: '0.7rem',
        weight: '600',
        transform: 'uppercase',
        wrap: 'nowrap',
        color: 'var(--theme-mode-background-color, #fff)',
        container: {
          padding: '6px 8px',
          background: { visible: true, color: 'var(--theme-mode-accent-color, #223163)' },
          border: { visible: true, style: 'none', width: '0', radius: '999px' },
        },
      },
    }
  ),
  txtMatchStateLabelMobile: TextParticleSpec.generateSchema(
    "Match State Label (Mobile)",
    "Pre / live / post label rendered as a badge. Used on mobile breakpoint (≤ 600px).",
    {
      defaultValues: {
        size: '0.7rem',
        weight: '600',
        transform: 'uppercase',
        wrap: 'nowrap',
        color: 'var(--theme-mode-background-color, #fff)',
        container: {
          padding: '6px 8px',
          background: { visible: true, color: 'var(--theme-mode-accent-color, #223163)' },
          border: { visible: true, style: 'none', width: '0', radius: '999px' },
        },
      },
    }
  ),

  // Team elements
  imgTeamA: ImageParticleSpec.generateSchema(
    "Home Team Logo",
    "The logo for the home team.",
    {
      defaultValues: {
        container: { width: '6rem', height: '6rem' },
        sizing: 'contain',
        fillWidth: true,
        fillHeight: true,
        // Team crests are pre-sized PNGs; skip the Cloudinary `general_medium`
        // transform (clxf) the particle would otherwise apply by default.
        role: 'none',
      },
    }
  ),
  imgTeamATablet: ImageParticleSpec.generateSchema(
    "Home Team Logo (Tablet)",
    "The logo for the home team. Used on tablet breakpoint (601-1000px).",
    {
      defaultValues: {
        container: { width: '6rem', height: '6rem' },
        sizing: 'contain',
        fillWidth: true,
        fillHeight: true,
        role: 'none',
      },
    }
  ),
  imgTeamAMobile: ImageParticleSpec.generateSchema(
    "Home Team Logo (Mobile)",
    "The logo for the home team. Used on mobile breakpoint (≤ 600px).",
    {
      defaultValues: {
        container: { width: '6rem', height: '6rem' },
        sizing: 'contain',
        fillWidth: true,
        fillHeight: true,
        role: 'none',
      },
    }
  ),
  txtHomeTeamName: TextParticleSpec.generateSchema(
    "Home Team Name",
    "Feed driven. Home team name.",
    { defaultValues: { size: '2.5rem', weight: '700', color: FG } }
  ),
  txtHomeTeamNameTablet: TextParticleSpec.generateSchema(
    "Home Team Name (Tablet)",
    "Feed driven. Home team name. Used on tablet breakpoint (601-1000px).",
    { defaultValues: { size: '1.75rem', weight: '500', color: FG } }
  ),
  txtHomeTeamNameMobile: TextParticleSpec.generateSchema(
    "Home Team Name (Mobile)",
    "Feed driven. Home team name. Used on mobile breakpoint (≤ 600px).",
    { defaultValues: { size: '1rem', weight: '500', color: FG } }
  ),
  txtHomeTeamScore: TextParticleSpec.generateSchema(
    "Home Team Score",
    "Feed driven. Home team primary score.",
    { defaultValues: { size: '1.25rem', weight: '400', color: HIGHLIGHT } }
  ),
  txtHomeTeamScoreTablet: TextParticleSpec.generateSchema(
    "Home Team Score (Tablet)",
    "Feed driven. Home team primary score. Used on tablet breakpoint (601-1000px).",
    { defaultValues: { size: '1rem', weight: '400', color: HIGHLIGHT } }
  ),
  txtHomeTeamScoreMobile: TextParticleSpec.generateSchema(
    "Home Team Score (Mobile)",
    "Feed driven. Home team primary score. Used on mobile breakpoint (≤ 600px).",
    { defaultValues: { size: '0.85rem', weight: '400', color: HIGHLIGHT } }
  ),
  txtHomeTeamSubscore: TextParticleSpec.generateSchema(
    "Home Team Subscore",
    "Feed driven. Optional secondary score line (e.g. cricket overs, soccer penalties).",
    { defaultValues: { size: '0.5rem', weight: '400', color: FG } }
  ),
  txtHomeTeamSubscoreTablet: TextParticleSpec.generateSchema(
    "Home Team Subscore (Tablet)",
    "Feed driven. Optional secondary score line (e.g. cricket overs, soccer penalties). Used on tablet breakpoint (601-1000px).",
    { defaultValues: { size: '0.75rem', weight: '500', color: FG } }
  ),
  txtHomeTeamSubscoreMobile: TextParticleSpec.generateSchema(
    "Home Team Subscore (Mobile)",
    "Feed driven. Optional secondary score line (e.g. cricket overs, soccer penalties). Used on mobile breakpoint (≤ 600px).",
    { defaultValues: { size: '0.5rem', weight: '500', color: FG } }
  ),
  imgTeamB: ImageParticleSpec.generateSchema(
    "Away Team Logo",
    "The logo for the away team.",
    {
      defaultValues: {
        container: { width: '6rem', height: '6rem' },
        sizing: 'contain',
        fillWidth: true,
        fillHeight: true,
        role: 'none',
      },
    }
  ),
  imgTeamBTablet: ImageParticleSpec.generateSchema(
    "Away Team Logo (Tablet)",
    "The logo for the away team. Used on tablet breakpoint (601-1000px).",
    {
      defaultValues: {
        container: { width: '6rem', height: '6rem' },
        sizing: 'contain',
        fillWidth: true,
        fillHeight: true,
        role: 'none',
      },
    }
  ),
  imgTeamBMobile: ImageParticleSpec.generateSchema(
    "Away Team Logo (Mobile)",
    "The logo for the away team. Used on mobile breakpoint (≤ 600px).",
    {
      defaultValues: {
        container: { width: '6rem', height: '6rem' },
        sizing: 'contain',
        fillWidth: true,
        fillHeight: true,
        role: 'none',
      },
    }
  ),
  txtAwayTeamName: TextParticleSpec.generateSchema(
    "Away Team Name",
    "Feed driven. Away team name.",
    { defaultValues: { size: '2.5rem', weight: '800', color: FG } }
  ),
  txtAwayTeamNameTablet: TextParticleSpec.generateSchema(
    "Away Team Name (Tablet)",
    "Feed driven. Away team name. Used on tablet breakpoint (601-1000px).",
    { defaultValues: { size: '1.75rem', weight: '500', color: FG } }
  ),
  txtAwayTeamNameMobile: TextParticleSpec.generateSchema(
    "Away Team Name (Mobile)",
    "Feed driven. Away team name. Used on mobile breakpoint (≤ 600px).",
    { defaultValues: { size: '1rem', weight: '500', color: FG } }
  ),
  txtAwayTeamScore: TextParticleSpec.generateSchema(
    "Away Team Score",
    "Feed driven. Away team primary score.",
    { defaultValues: { size: '1.25rem', weight: '700', color: HIGHLIGHT } }
  ),
  txtAwayTeamScoreTablet: TextParticleSpec.generateSchema(
    "Away Team Score (Tablet)",
    "Feed driven. Away team primary score. Used on tablet breakpoint (601-1000px).",
    { defaultValues: { size: '1rem', weight: '400', color: HIGHLIGHT } }
  ),
  txtAwayTeamScoreMobile: TextParticleSpec.generateSchema(
    "Away Team Score (Mobile)",
    "Feed driven. Away team primary score. Used on mobile breakpoint (≤ 600px).",
    { defaultValues: { size: '0.85rem', weight: '400', color: HIGHLIGHT } }
  ),
  txtAwayTeamSubscore: TextParticleSpec.generateSchema(
    "Away Team Subscore",
    "Feed driven. Optional secondary score line.",
    { defaultValues: { size: '0.5rem', weight: '400', color: FG } }
  ),
  txtAwayTeamSubscoreTablet: TextParticleSpec.generateSchema(
    "Away Team Subscore (Tablet)",
    "Feed driven. Optional secondary score line. Used on tablet breakpoint (601-1000px).",
    { defaultValues: { size: '0.75rem', weight: '500', color: FG } }
  ),
  txtAwayTeamSubscoreMobile: TextParticleSpec.generateSchema(
    "Away Team Subscore (Mobile)",
    "Feed driven. Optional secondary score line. Used on mobile breakpoint (≤ 600px).",
    { defaultValues: { size: '0.5rem', weight: '500', color: FG } }
  ),

  // Result
  txtResultLabel: TextParticleSpec.generateSchema(
    "Result Label",
    "Feed driven. Match result summary.",
    { defaultValues: { size: '1rem', weight: '600', color: FG } }
  ),
  txtResultLabelTablet: TextParticleSpec.generateSchema(
    "Result Label (Tablet)",
    "Feed driven. Match result summary. Used on tablet breakpoint (601-1000px).",
    { defaultValues: { size: '1rem', weight: '600', color: FG } }
  ),
  txtResultLabelMobile: TextParticleSpec.generateSchema(
    "Result Label (Mobile)",
    "Feed driven. Match result summary. Used on mobile breakpoint (≤ 600px).",
    { defaultValues: { size: '1rem', weight: '600', color: FG } }
  ),

  // Weather (optional)
  imgWeatherIcon: ImageParticleSpec.generateSchema(
    "Weather Icon",
    "Weather condition icon.",
    {
      defaultValues: {
        container: { width: '2rem', height: '2rem' },
        sizing: 'contain',
        // Weather icons are pre-sized PNGs hosted on S3; skip the Cloudinary
        // `general_medium` transform the particle would otherwise apply.
        role: 'none',
      },
    }
  ),
  txtTemperatureLabel: TextParticleSpec.generateSchema(
    "Temperature Label",
    "Feed driven. Temperature display e.g. 9.8°C.",
    { defaultValues: { size: '1.25rem', weight: '700', color: HIGHLIGHT } }
  ),
  txtWeatherLabel: TextParticleSpec.generateSchema(
    "Weather Label",
    "Feed driven. Condition text e.g. Clear, Rainy.",
    { defaultValues: { size: '0.75rem', weight: '500', color: ACCENT } }
  ),

  // CTA
  btnCTA: generateArraySchema(
    "CTA Buttons",
    "Action buttons (e.g. Scorecard, Commentary).",
    ButtonParticleSpec.generateSchema("CTA Button", "A call-to-action button.", {
      defaultValues: {
        container: {
          padding: '0.5rem 1rem',
          border: {
            visible: true,
            radius: '0.375rem',
            color: '#DDD',
            style: 'solid',
            width: '1px',
          },
          background: { visible: true },
          fillHeight: false,
          fillWidth: false,
        },
        text: { size: '0.875rem', weight: '600', transform: 'uppercase' },
      },
    })
  ),

  // Atomic style fields (piped to --mh-* CSS variables)
  paddingRoot: generateStringSchema("Root Padding", "Inner padding of the match-header root.", {
    defaultValue: '1.5rem',
  }),
  paddingRootTablet: generateStringSchema(
    "Root Padding (Tablet)",
    "Inner padding under the tablet container query (601-1000px).",
    { defaultValue: '1.25rem' }
  ),
  paddingRootCompact: generateStringSchema(
    "Root Padding (Compact)",
    "Inner padding under the mobile container query (≤ 600px).",
    { defaultValue: '1rem' }
  ),
  gapRoot: generateStringSchema("Root Gap", "Vertical gap between top-level rows.", {
    defaultValue: '1rem',
  }),
  gapRootCompact: generateStringSchema(
    "Root Gap (Compact)",
    "Top-level gap under tablet/mobile container queries.",
    { defaultValue: '0.75rem' }
  ),
  gapScorecard: generateStringSchema(
    "Scorecard Gap",
    "Horizontal gap between teams / logos / center column on the scorecard row.",
    { defaultValue: '1.5rem' }
  ),
  paddingScorecardTopCompact: generateStringSchema(
    "Scorecard Top Padding (Compact)",
    "Extra top padding above the scorecard on mobile (≤ 600px). Adds breathing room between the info bar and the match status badge.",
    { defaultValue: '2rem' }
  ),
  gapScorecardCompact: generateStringSchema(
    "Scorecard Gap (Compact)",
    "Scorecard gap under tablet/mobile container queries.",
    { defaultValue: '1rem' }
  ),
  gapInfoBar: generateStringSchema(
    "Info Bar Gap",
    "Vertical gap between competition / date / location lines.",
    { defaultValue: '0.25rem' }
  ),
  gapWeatherDisplay: generateStringSchema(
    "Weather Display Gap",
    "Gap between the temperature column and the weather icon.",
    { defaultValue: '0.5rem' }
  ),

  // ── Cricket-only: over-by-over cards ──
  // JSON string because the Bison data mapper can't handle nested object arrays.
  oversJson: generateStringSchema(
    "Overs JSON",
    "JSON-encoded array of over card objects. Each has balls[] and txtOverLabel."
  ),

  // ── Decorative compound boxes ──
  // Ball boxes are colored from the shared cricket palette by outcome
  // (matching the Ball by Ball card). Setting a background here overrides
  // the palette for that scoring state; border/padding always apply.
  ballBoxContainer: applyAnnotationsToSchema(
    generateContainerSchema(
      "Ball Box",
      "Container styling for a non-scoring ball. Background defaults to the cricket palette by outcome; set one here to override."
    ),
    {
      border: { radius: '0.25rem' },
      padding: '0.25rem',
    }
  ),
  ballBoxScoringContainer: applyAnnotationsToSchema(
    generateContainerSchema(
      "Ball Box (Scoring)",
      "Container styling for a scoring ball. Background defaults to the cricket palette by outcome; set one here to override."
    ),
    {
      border: { radius: '0.25rem' },
      padding: '0.25rem',
    }
  ),

  // ── Atomic style fields (piped to --cmh-* CSS variables) ──
  dividerColor: generateColorSchema(
    "Divider Color",
    "Color of the horizontal divider above the overs scroller.",
    { defaultValue: HIGHLIGHT }
  ),
  dividerThickness: generateStringSchema(
    "Divider Thickness",
    "Border thickness of the divider above the overs scroller.",
    { defaultValue: '2px' }
  ),
  dividerMargin: generateStringSchema(
    "Divider Margin",
    "Margin around the divider (vertical spacing + horizontal inset).",
    { defaultValue: '40px 2.5rem' }
  ),
  gapOversWrap: generateStringSchema(
    "Overs Wrap Gap",
    "Vertical gap between the divider and the overs scroller.",
    { defaultValue: '1rem' }
  ),
  paddingOversWrapCompact: generateStringSchema(
    "Overs Wrap Top Padding (Compact)",
    "Top padding above the overs scroller on mobile (≤ 600px) — compensates for the hidden divider so the team names don't crowd the overs section.",
    { defaultValue: '1.5rem' }
  ),
  gapOversScroll: generateStringSchema(
    "Overs Scroll Gap",
    "Horizontal gap between adjacent over cards.",
    { defaultValue: '4rem' }
  ),
  gapOversScrollCompact: generateStringSchema(
    "Overs Scroll Gap (Compact)",
    "Horizontal gap between adjacent over cards on mobile (≤ 600px).",
    { defaultValue: '2rem' }
  ),
  paddingOversScroll: generateStringSchema(
    "Overs Scroll Padding",
    "Inner horizontal padding of the overs scroller.",
    { defaultValue: '0 3rem' }
  ),
  paddingOversScrollCompact: generateStringSchema(
    "Overs Scroll Padding (Compact)",
    "Inner horizontal padding of the overs scroller on mobile (≤ 600px).",
    { defaultValue: '0 3rem' }
  ),
  gapOverBallsRow: generateStringSchema(
    "Over Balls Row Gap",
    "Horizontal gap between the balls row and the over label inside each over card.",
    { defaultValue: '0.5rem' }
  ),
  gapOverBalls: generateStringSchema(
    "Over Balls Gap",
    "Horizontal gap between adjacent ball boxes.",
    { defaultValue: '0.375rem' }
  ),
  widthOverCard: generateStringSchema(
    "Over Card Width",
    "Fixed width of each over card on desktop/tablet.",
    { defaultValue: '16rem' }
  ),
  colorBallBox: generateColorSchema(
    "Ball Box Text Color",
    "Overrides the palette text color inside a non-scoring ball box (leave unset to use the cricket palette)."
  ),
  colorBallBoxScoring: generateColorSchema(
    "Ball Box Text Color (Scoring)",
    "Overrides the palette text color inside a scoring ball box (leave unset to use the cricket palette)."
  ),
  colorOverLabel: generateColorSchema(
    "Over Label Color",
    "Text color of the over label (e.g. \"OV 12\") shown beside each over's ball boxes.",
    { defaultValue: ACCENT }
  ),
  sizeOverLabel: generateStringSchema(
    "Over Label Size",
    "Font size of the over label (e.g. \"OV 12\") shown beside each over's ball boxes.",
    { defaultValue: '0.75rem' }
  ),
  weightOverLabel: generateStringSchema(
    "Over Label Weight",
    "Font weight of the over label (e.g. \"OV 12\") shown beside each over's ball boxes.",
    { defaultValue: '500' }
  ),
}));
